interface Distribution {
  top1: number
  top3: number
  top5: number
  top10: number
  top30: number
  notRanked: number
}

interface ProgressBarProps {
  distribution: Distribution
  totalDelta: number
}

const segments = [
  { keys: ['top1', 'top3'] as const, color: '#10B981', label: 'Top 1-3' },
  { keys: ['top10'] as const, color: '#3B82F6', label: 'Top 4-10' },
  { keys: ['top30'] as const, color: '#FBBF24', label: 'Top 11-30' },
  { keys: ['notRanked'] as const, color: '#94A3B8', label: '31-100' },
]

export default function ProgressBar({ distribution, totalDelta }: ProgressBarProps) {
  const total = distribution.top1 + distribution.top3 + distribution.top10 + distribution.top30 + distribution.notRanked
  if (total === 0) return null

  const segmentValues = segments.map(seg => ({
    ...seg,
    count: seg.keys.reduce((sum, k) => sum + (distribution[k] ?? 0), 0),
  }))

  const deltaColor = totalDelta > 0 ? 'text-emerald-600' : totalDelta < 0 ? 'text-red-500' : 'text-[#8B8FA7]'
  const deltaPrefix = totalDelta > 0 ? '+' : ''

  return (
    <div className="bg-white border border-[#E2E4ED] rounded-xl p-5">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-3">
          {segmentValues.map(seg => (
            <div key={seg.label} className="flex items-center gap-1.5">
              <span className="w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: seg.color }} />
              <span className="text-[11px] text-[#555A73] font-medium">{seg.label}</span>
              <span className="text-[11px] font-bold text-[#1B2559] tabular-nums">{seg.count}</span>
            </div>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-[#8B8FA7]">Evolution globale</span>
          <span className={`text-lg font-bold tabular-nums ${deltaColor}`}>
            {deltaPrefix}{totalDelta}
          </span>
        </div>
      </div>
      <div className="flex h-3 rounded-full overflow-hidden bg-[#F4F5FA]">
        {segmentValues.map(seg => {
          if (seg.count === 0) return null
          const pct = (seg.count / total) * 100
          return (
            <div
              key={seg.label}
              className="h-full transition-all duration-500"
              style={{
                width: `${pct}%`,
                backgroundColor: seg.color,
                minWidth: seg.count > 0 ? '4px' : '0',
              }}
              title={`${seg.label}: ${seg.count} (${Math.round(pct)}%)`}
            />
          )
        })}
      </div>
    </div>
  )
}
